import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowDown, ArrowUp, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { isAxiosError } from 'axios'
import { Button } from '@quebon/ui/components/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@quebon/ui/components/dialog'
import { sharedMediaApi, type SharedMediaResponse } from '@/api/admin'

const TYPE_LABELS = { video: '동영상', text: '텍스트', image: '이미지', file: '파일' }

export function SharedMediaOrderDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<SharedMediaResponse[]>([])
  const [previousIds, setPreviousIds] = useState<number[]>([])
  const [stale, setStale] = useState(false)
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'shared-media', 'order'],
    queryFn: async () => {
      const response = await sharedMediaApi.getAll()
      if (!response.data.success || !response.data.data) throw new Error('Unable to load resources')
      return response.data.data
    },
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    gcTime: 0,
  })

  useEffect(() => {
    if (data) {
      setDraft(data)
      setPreviousIds(data.map(item => item.id))
      setStale(false)
    }
  }, [data])

  const save = useMutation({
    mutationFn: async () => {
      const response = await sharedMediaApi.reorder(draft.map(item => item.id), previousIds)
      if (!response.data.success) throw new Error('Unable to save order')
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'shared-media'] })
      toast.success('자료 순서를 저장했습니다.')
      onClose()
    },
    onError: (error: unknown) => {
      const code = isAxiosError<{ error?: { code?: string } }>(error)
        ? error.response?.data?.error?.code
        : undefined
      if (code === 'SHARED_MEDIA_ORDER_STALE') {
        setStale(true)
        toast.error('자료 목록이나 순서가 변경되었습니다. 최신 목록을 불러와 다시 조정해 주세요.')
      } else {
        toast.error('순서를 저장하지 못했습니다. 다시 시도해 주세요.')
      }
    },
  })

  function move(index: number, direction: -1 | 1) {
    setDraft(current => {
      const next = [...current]
      const target = index + direction
      if (target < 0 || target >= next.length) return current
      ;[next[index], next[target]] = [next[target], next[index]]
      return next
    })
  }

  const changed = draft.some((item, index) => item.id !== previousIds[index])
  return (
    <Dialog open onOpenChange={open => { if (!open && !save.isPending) onClose() }}>
      <DialogContent className="sm:max-w-2xl" showCloseButton={!save.isPending}>
        <DialogHeader>
          <DialogTitle>자료 순서 조정</DialogTitle>
          <DialogDescription>
            위·아래 버튼으로 이동한 뒤 저장하세요. LMS에도 공개된 자료가 같은 순서로 표시됩니다. 새 자료는 맨 위에 추가됩니다.
          </DialogDescription>
        </DialogHeader>
        {isLoading ? <p role="status">자료를 불러오는 중입니다.</p> : isError ? (
          <div className="flex items-center gap-4"><p role="alert">자료를 불러오지 못했습니다.</p><Button variant="outline" onClick={() => void refetch()}>다시 시도</Button></div>
        ) : (
          <ol className="flex max-h-[55vh] flex-col gap-2 overflow-y-auto" aria-label="자료 표시 순서">
            {draft.map((item, index) => (
              <li key={item.id} className="flex items-center gap-3 border-b border-border py-2">
                <span className="w-8 shrink-0 text-center tabular-nums text-muted-foreground">{index + 1}</span>
                <div className="min-w-0 flex-1"><p className="truncate" title={item.title}>{item.title}</p><p className="text-sm text-muted-foreground">{TYPE_LABELS[item.mediaType]}</p></div>
                <Button variant="outline" size="icon" aria-label={`${item.title} 위로 이동`} disabled={index === 0 || save.isPending || stale} onClick={() => move(index, -1)}><ArrowUp /></Button>
                <Button variant="outline" size="icon" aria-label={`${item.title} 아래로 이동`} disabled={index === draft.length - 1 || save.isPending || stale} onClick={() => move(index, 1)}><ArrowDown /></Button>
              </li>
            ))}
            {draft.length === 0 && <li>등록된 자료가 없습니다.</li>}
          </ol>
        )}
        {stale && <div className="flex items-center gap-4"><p role="alert">변경된 최신 목록을 불러온 뒤 다시 조정해 주세요.</p><Button variant="outline" onClick={() => void refetch()}>최신 목록 불러오기</Button></div>}
        <DialogFooter>
          <Button variant="outline" disabled={save.isPending} onClick={onClose}>취소</Button>
          <Button disabled={!changed || isLoading || isError || stale || save.isPending} onClick={() => save.mutate()}>
            {save.isPending && <Loader2 className="animate-spin" data-icon="inline-start" />}{save.isPending ? '저장 중' : '순서 저장'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
